/**
 * Pure swap math utility functions.
 *
 * All functions are side-effect-free and easy to unit test.
 * Provides client-side approximations for swap quotes.
 *
 * Note: For production accuracy, integrate Uniswap V4 Quoter contract.
 * These approximations are sufficient for display purposes.
 *
 * Story 5.1 Task 4
 */

import { type Hex, encodeAbiParameters, keccak256 } from "viem";

/** Q96 = 2^96 — the fixed-point denominator for sqrtPriceX96. */
const Q96 = 2n ** 96n;

/** Q192 = 2^192 — used for price = sqrtPriceX96^2 / Q192. */
const Q192 = 2n ** 192n;

/** Precision scale for bigint → float conversion (10^18). */
const PRECISION_SCALE = 10n ** 18n;

/** Fee denominator in pips (1,000,000 = 100%). */
const PIPS_DENOMINATOR = 1_000_000n;

/** Slippage denominator in basis points (10,000 = 100%). */
const BPS_DENOMINATOR = 10_000n;

/**
 * Compute the Uniswap V4 pool ID from pool key components.
 * poolId = keccak256(abi.encode(currency0, currency1, fee, tickSpacing, hooks))
 */
export function computePoolId(
	token0: `0x${string}`,
	token1: `0x${string}`,
	fee: number,
	tickSpacing: number,
	hooks: `0x${string}`
): Hex {
	return keccak256(
		encodeAbiParameters(
			[
				{ type: "address" },
				{ type: "address" },
				{ type: "uint24" },
				{ type: "int24" },
				{ type: "address" },
			],
			[token0, token1, fee, tickSpacing, hooks]
		)
	);
}

/**
 * Convert sqrtPriceX96 (Q64.96 format) to a human-readable price.
 *
 * price = (sqrtPriceX96 / 2^96)^2 * 10^(token0Decimals - token1Decimals)
 */
export function sqrtPriceX96ToPrice(
	sqrtPriceX96: bigint,
	token0Decimals: number,
	token1Decimals: number
): number {
	if (sqrtPriceX96 === 0n) return 0;

	// Pure bigint math: price_scaled = sqrtPriceX96^2 * PRECISION_SCALE / Q192
	// This avoids Number(sqrtPriceX96) which loses precision for 160-bit values.
	const priceScaled = (sqrtPriceX96 * sqrtPriceX96 * PRECISION_SCALE) / Q192;

	// Convert to float and apply decimal adjustment
	const decimalAdjustment = 10 ** (token0Decimals - token1Decimals);
	return (Number(priceScaled) / Number(PRECISION_SCALE)) * decimalAdjustment;
}

/**
 * Estimate swap output using a constant-product approximation from current tick/liquidity.
 *
 * @deprecated Use the V4 Quoter contract via useSwapQuote instead.
 * This approximation returns 0 when getLiquidity()=0 at a tick boundary,
 * which is a false positive — the V4 swap engine handles tick crossing correctly.
 * The on-chain Quoter simulates the actual swap path and returns accurate quotes.
 *
 * Uses: amountOut ≈ (amountIn * (1 - feeBps/1_000_000)) * liquidity / (liquidity + amountIn)
 * adjusted by the sqrtPriceX96 ratio.
 */
export function estimateSwapOutput(
	amountIn: bigint,
	sqrtPriceX96: bigint,
	liquidity: bigint,
	feeBps: number,
	zeroForOne: boolean
): { amountOut: bigint; priceImpact: number } {
	if (amountIn === 0n || liquidity === 0n) {
		return { amountOut: 0n, priceImpact: 0 };
	}

	// Apply fee: amountInAfterFee = amountIn * (1_000_000 - fee) / 1_000_000
	const amountInAfterFee = (amountIn * (PIPS_DENOMINATOR - BigInt(feeBps))) / PIPS_DENOMINATOR;

	// Constant product approximation:
	// Virtual reserves derived from liquidity and sqrtPriceX96:
	//   reserve0 = liquidity * 2^96 / sqrtPriceX96
	//   reserve1 = liquidity * sqrtPriceX96 / 2^96
	let reserveIn: bigint;
	let reserveOut: bigint;

	if (zeroForOne) {
		reserveIn = (liquidity * Q96) / sqrtPriceX96;
		reserveOut = (liquidity * sqrtPriceX96) / Q96;
	} else {
		reserveIn = (liquidity * sqrtPriceX96) / Q96;
		reserveOut = (liquidity * Q96) / sqrtPriceX96;
	}

	if (reserveIn === 0n || reserveOut === 0n) {
		return { amountOut: 0n, priceImpact: 0 };
	}

	// Constant product: amountOut = reserveOut * amountInAfterFee / (reserveIn + amountInAfterFee)
	const amountOut = (reserveOut * amountInAfterFee) / (reserveIn + amountInAfterFee);

	// Price impact via scaled bigint math (avoids Number overflow for large reserves)
	const scaledMidPrice = (reserveOut * PRECISION_SCALE) / reserveIn;
	const scaledExecPrice = (amountOut * PRECISION_SCALE) / amountInAfterFee;
	const priceImpact =
		scaledMidPrice > 0n
			? Math.abs(Number(scaledMidPrice - scaledExecPrice) / Number(scaledMidPrice)) * 100
			: 0;

	return { amountOut, priceImpact };
}

/**
 * Calculate minimum output after slippage tolerance.
 * minimumOutput = amountOut * (10000 - slippageBps) / 10000
 */
export function calculateMinimumOutput(amountOut: bigint, slippageBps: number): bigint {
	return (amountOut * (BPS_DENOMINATOR - BigInt(slippageBps))) / BPS_DENOMINATOR;
}

/**
 * Calculate price impact as a percentage.
 * Returns 0 if mid price is 0. Always returns a non-negative value.
 */
export function calculatePriceImpact(executionPrice: number, midPrice: number): number {
	if (midPrice === 0) return 0;
	return Math.abs((executionPrice - midPrice) / midPrice) * 100;
}
